import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { isIOSWebView } from "../lib/ios_focus_guard"

const log = createLogger('KeyboardCover')

/**
 * iOS에서 fixed bottom-0 요소를 키보드 뒤로 이동시키는 컨트롤러
 *
 * 배경:
 * - WKWebView는 키보드 표시 시 visual viewport를 자동 축소
 * - 이로 인해 fixed bottom-0 요소가 자동으로 키보드 위로 올라감
 * - Android의 adjustNothing 모드에서는 요소가 화면 하단에 유지되어 키보드에 가려짐
 *
 * 이 컨트롤러는:
 * - iOS에서만 동작하며 WKWebView의 자동 이동을 상쇄
 * - 요소를 키보드 높이만큼 아래로 밀어서 키보드가 가리게 함
 * - Android와 동일한 UX 제공 (키보드가 하단 요소를 가림)
 *
 * 중요: CSS transition 없이 즉시 적용!
 * WKWebView가 이미 애니메이션을 처리했으므로 추가 애니메이션은 충돌을 일으킴
 */
export default class extends Controller {
  connect() {
    // iOS WebView에서만 동작
    if (!isIOSWebView()) {
      log.debug('Not iOS WebView - keyboard cover disabled')
      return
    }

    this.handleIOSKeyboard = this.onIOSKeyboardChange.bind(this)
    window.addEventListener('ios:keyboardChange', this.handleIOSKeyboard)

    // GPU 가속을 위한 will-change 힌트 (transition 없음!)
    this.element.style.willChange = 'transform'
    log.debug('KeyboardCover connected')
  }

  disconnect() {
    if (!isIOSWebView()) return

    window.removeEventListener('ios:keyboardChange', this.handleIOSKeyboard)
    this.element.style.transform = ''
    this.element.style.willChange = ''
  }

  /**
   * iOS 키보드 이벤트 핸들러
   * WKWebView가 요소를 키보드 위로 올렸으므로, 다시 아래로 밀어서 키보드가 가리게 함
   *
   * 중요: transition 없이 즉시 적용!
   * Swift에서 이벤트가 dispatch될 때 WKWebView 애니메이션은 이미 완료됨
   * 우리는 단순히 반대 방향으로 밀기만 하면 됨
   */
  onIOSKeyboardChange(event) {
    // 바텀시트가 이 요소를 제어 중이면 무시
    // bottom_sheet_controller가 translateY(100%)로 숨긴 상태에서
    // 키보드 이벤트가 transform을 덮어쓰는 것을 방지
    if (this.element.dataset.bottomSheetControlled === 'true') {
      log.debug('Keyboard cover: skipping - bottom sheet is controlling this element')
      return
    }

    const { height, visible } = event.detail

    if (visible && height > 0) {
      // 키보드 높이만큼 아래로 밀어서 키보드가 가리게 함 (즉시, 애니메이션 없음)
      this.element.style.transform = `translateY(${height}px)`
      log.debug(`Keyboard cover: pushing down ${height}px (no transition)`)
    } else {
      this.element.style.transform = ''
      log.debug('Keyboard cover: reset')
    }
  }
}
